// src/pages/Dashboard.jsx
import { useMemo } from "react";
import { startOfMonth, format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useMe } from "../../../hooks/useMe";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import AuthCard from "../../../components/auth/AuthCard";
import TodayTasksCard from "./components/TodayTasksCard";
import RecentWorkoutsCard from "./components/RecentWorkoutsCard";
import BottomNav from "../../../components/common/BottomNav";
import WeightChartCard from "./components/WeightChartCard";
import HealthRecordsCard from "./components/HealthRecordsCard";
import GlucoseCard from "./components/GlucoseCard";
import {
    useBpMonth,
    useGlucoseMonth,
    useWeightMonth,
    useBodyFatMonth,
    useDashboardScore,
} from "../../../hooks/useHealthMetrics";
import { useProfile } from "../../../hooks/useProfile";

import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
} from "recharts";

const getBodyFatMass = (r) =>
    r?.body_fat ?? r?.body_fat_mass ?? r?.bodyFatMass ?? r?.fat_mass ?? r?.bodyFat ?? r?.value;

const EmptyRecordCard = ({ message, onClick, className = "h-32" }) => (
    <div
        onClick={onClick}
        className={`${className} flex flex-col items-center justify-center 
                   bg-gray-50 rounded-2xl border-2 border-gray-200 border-dashed 
                   text-gray-400 cursor-pointer transition hover:bg-gray-100`}
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
        </svg>
        <p className="mt-2 text-xs font-medium">{message}</p>
    </div>
);

const BmiCard = ({ bmiData, onProfileClick, onWeightClick }) => {
    // 1. 키 정보가 없을 때
    if (!bmiData.hasHeight) {
        return (
            <div
                onClick={onProfileClick}
                className="w-full bg-blue-50 rounded-2xl p-5 flex items-center justify-between shadow-sm border border-blue-100 cursor-pointer hover:bg-blue-100 transition"
            >
                <div>
                    <p className="text-sm text-blue-500 font-bold">BMI 분석 불가</p>
                    <p className="text-gray-600 text-sm mt-1">
                        정확한 분석을 위해 <br />
                        <strong>키(cm) 정보</strong>를 입력해주세요.
                    </p>
                </div>
                <div className="bg-white p-2 rounded-full text-blue-500">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </div>
            </div>
        );
    }

    // 2. 체중 기록이 없을 때
    if (!bmiData.hasWeight) {
        return (
            <div
                onClick={onWeightClick}
                className="w-full bg-gray-50 rounded-2xl p-5 flex items-center justify-between shadow-sm border border-gray-200 cursor-pointer hover:bg-gray-100 transition"
            >
                <div>
                    <p className="text-sm text-gray-500 font-bold">
                        BMI 데이터 부족
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                        최근 <strong>체중 기록</strong>이 없습니다.
                    </p>
                </div>
                <div className="bg-white p-2 rounded-full text-gray-400">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full rounded-2xl p-5 flex flex-row items-center justify-between border border-gray-200">
            <div>
                <p className="text-sm text-blue-500 font-bold mb-1">
                    나의 BMI 지수
                </p>
                <div className="flex items-end gap-2">
                    <span className="text-3xl font-extrabold text-gray-800">
                        {bmiData.bmi}
                    </span>
                    <span className="text-sm font-medium text-gray-500 mb-1">
                        ({bmiData.height}cm / {bmiData.weight}kg)
                    </span>
                </div>
            </div>
            <div
                className={`px-3 py-1 rounded-lg text-sm font-bold whitespace-nowrap
        ${bmiData.status === "비만"
                        ? "bg-red-100 text-red-600"
                        : bmiData.status === "과체중"
                            ? "bg-orange-100 text-orange-600"
                            : bmiData.status === "정상"
                                ? "bg-green-100 text-green-600"
                                : "bg-gray-200 text-gray-600"
                    }`}
            >
                {bmiData.status}
            </div>
        </div>
    );
};

const FitnessRadarCard = ({ userName, score, data }) => {
    const chartData = data?.map((item) => ({
        ...item,
        A: item.A ?? 0,
    })) ?? [
            { subject: "벤치프레스", A: 0, fullMark: 100 },
            { subject: "데드리프트", A: 0, fullMark: 100 },
            { subject: "근파워", A: 0, fullMark: 100 },
            { subject: "근지구력", A: 0, fullMark: 100 },
            { subject: "근력", A: 0, fullMark: 100 },
            { subject: "스쿼트", A: 0, fullMark: 100 },
            { subject: "심폐지구력", A: 0, fullMark: 100 },
        ];

    return (
        <div className="w-full bg-[#F4F8FF] rounded-[24px] px-2 pt-6 pb-7 flex flex-col items-center">
            <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                        cx="50%"
                        cy="52%"
                        outerRadius="72%"
                        data={chartData}
                        margin={{ top: 4, right: 24, bottom: 4, left: 24 }}
                    >
                        <PolarGrid gridType="polygon" />

                        <PolarAngleAxis
                            dataKey="subject"
                            tick={{
                                fill: "#6B7280",
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                            tickSize={18}
                        />

                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, 100]}
                            ticks={[20, 40, 60, 80, 100]}
                            tick={{ fill: "#9CA3AF", fontSize: 10 }}
                            axisLine={false}
                            tickLine={false}
                        />

                        <Radar
                            name="체력"
                            dataKey="A"
                            stroke="#86b2ff"
                            fill="#a6c8ff"
                            fillOpacity={0.7}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </div>

            <p className="mt-4 text-[17px] font-bold text-gray-800 text-center">
                {userName} 님의 체력 평균{" "}
                <span className="text-[#4A8AFF]">
                    {score != null ? `${score}점` : "측정 필요"}
                </span>
            </p>
        </div>
    );
};


const HealthScoreCard = ({
    strengthScore,
    strengthDiff,
    cardioScore,
    cardioStatus,
}) => {
    return (
        <div className="w-full">
            <p className="text-base md:text-lg leading-tight pb-2 font-bold">
                헬스 스코어
            </p>

            <div className="w-full bg-white rounded-2xl border border-gray-200 py-6 px-2 flex items-center justify-between">
                <div className="flex-1 flex flex-col items-center justify-center border-r border-gray-100">
                    <p className="text-sm font-bold text-gray-800 mb-2">종합 근력 점수</p>
                    <p className="text-[32px] md:text-4xl font-light text-[#4A8AFF] mb-2">
                        {strengthScore ?? "-"}
                    </p>
                    <p className="text-xs text-gray-700 font-medium">
                        지난 주 대비{" "}
                        <span className="text-[#32D069]">
                            {strengthDiff ?? "-"}
                        </span>
                    </p>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center">
                    <p className="text-sm font-bold text-gray-800 mb-2">심폐지구력</p>
                    <p className="text-[32px] md:text-4xl font-light text-[#4A8AFF] mb-2">
                        {cardioScore ?? "-"}
                    </p>
                    <p className="text-xs text-gray-700 font-medium">
                        ml/kg/min{" "}
                        <span className="text-[#32D069]">
                            ({cardioStatus ?? "측정 필요"})
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default function Dashboard() {
    const navigate = useNavigate();
    const { data: me } = useMe();
    const { data: profile } = useProfile();
    const ready = useLoadingGate();

    const today = new Date();
    const month = startOfMonth(today);

    // 월간 데이터 3종 불러오기
    const { data: weightMonth = [] } = useWeightMonth(month);
    const { data: bodyFatMonth = [] } = useBodyFatMonth(month);
    const { data: bpMonth = [] } = useBpMonth(month);
    const { data: glucoseMonth = [] } = useGlucoseMonth(month);
    const { data: dashboardScore } = useDashboardScore();

    const lastLoginRaw = me?.lastLogin ?? me?.last_login ?? null;
    const lastLoginText = lastLoginRaw
        ? format(new Date(lastLoginRaw), "yyyy-MM-dd HH:mm:ss")
        : null;

    // 차트 데이터 변환 유틸 (체중 그래프용 - 월간)
    const toDailyLastSeries = (rows, keys) => {
        const map = new Map();
        rows.forEach((r) => {
            const d = new Date(r.dt);
            const dd = format(d, "dd");
            const prev = map.get(dd);
            if (!prev || new Date(prev.dt) < d) map.set(dd, r);
        });
        return [...map.entries()]
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([dd, r]) => {
                const base = { label: dd };
                keys.forEach((k) => {
                    base[k] = r[k];
                });
                return base;
            });
    };

    const latestOf = (rows) =>
        rows.length
            ? rows.reduce((a, b) =>
                new Date(a.dt) > new Date(b.dt) ? a : b,
            )
            : null;

    // 🔹 혈압/혈당용: "최근 1개"만 시리즈로 만드는 유틸
    const toDailyLastValueSeries = (rows, getValue) => {
        const map = new Map();
        rows.forEach((r) => {
            const d = new Date(r.dt);
            const dd = format(d, "dd");
            const prev = map.get(dd);
            if (!prev || new Date(prev.dt) < d) map.set(dd, r);
        });
        return [...map.entries()]
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([dd, r]) => ({
                label: dd,
                value: getValue(r),
            }));
    };

    const toSingleLatestSeries = (rows, keys) => {
        if (!rows || rows.length === 0) return [];
        const latest = rows.reduce((a, b) =>
            new Date(a.dt) > new Date(b.dt) ? a : b,
        );
        const d = new Date(latest.dt);
        const dd = format(d, "dd");
        const base = { label: dd };
        keys.forEach((k) => {
            base[k] = latest[k];
        });
        return [base];
    };

    const weightSeries = useMemo(
        () => toDailyLastSeries(weightMonth, ["weight"]),
        [weightMonth],
    );
    const latestWeight = useMemo(
        () => latestOf(weightMonth),
        [weightMonth],
    );

    const bodyFatSeries = useMemo(
        () => toDailyLastValueSeries(bodyFatMonth, getBodyFatMass),
        [bodyFatMonth],
    );
    const latestBodyFat = useMemo(
        () => latestOf(bodyFatMonth),
        [bodyFatMonth],
    );

    const bpSeries = useMemo(
        () => toSingleLatestSeries(bpMonth, ["sys", "dia"]),
        [bpMonth],
    );
    const latestBp = useMemo(() => latestOf(bpMonth), [bpMonth]);

    const glucoseSeries = useMemo(
        () => toSingleLatestSeries(glucoseMonth, ["fasting"]),
        [glucoseMonth],
    );
    const latestGlucose = useMemo(
        () => latestOf(glucoseMonth),
        [glucoseMonth],
    );

    const bmiData = useMemo(() => {
        const profileHeight =
            profile?.height ??
            profile?.height_cm ??
            profile?.heightCm ??
            null;
        const meHeight =
            me?.height ?? me?.height_cm ?? me?.heightCm ?? null;
        const height = profileHeight ?? meHeight;

        const profileWeight =
            profile?.weight ??
            profile?.weight_kg ??
            profile?.weightKg ??
            null;
        const weight = latestWeight?.weight ?? profileWeight;

        const sex =
            profile?.sex ??
            profile?.gender ??
            me?.sex ??
            me?.gender ??
            null;

        const hasHeight = !!(height && height > 0);
        const hasWeight = !!(weight && weight > 0);

        if (!hasHeight || !hasWeight) {
            return { hasHeight, hasWeight, sex };
        }

        const heightM = height / 100;
        const bmiValue = (weight / (heightM * heightM)).toFixed(1);

        let status = "정상";
        const bmiNum = Number(bmiValue);
        if (bmiNum < 18.5) status = "저체중";
        else if (bmiNum >= 25) status = "비만";
        else if (bmiNum >= 23) status = "과체중";

        return {
            hasHeight: true,
            hasWeight: true,
            bmi: bmiValue,
            status,
            height,
            weight,
            sex,
        };
    }, [me, profile, latestWeight]);

    const handleClick = (addr) => navigate(`/${addr}`);

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    profile={() => handleClick("profile")}
                >
                    <div className="flex flex-col flex-1 pb-14">
                        <header className="py-9">
                            <p className="text-2xl md:text-3xl leading-tight">
                                오늘도 건강하세요,
                                <br />
                                <span className="font-bold">
                                    {me?.name ?? "회원"} 님
                                </span>
                            </p>

                            <p className="text-sm md:text-lg text-[#4A8AFF] mt-2">
                                최근 접속 시각 2025-06-01 12:00:00
                            </p>
                        </header>

                        <section className="flex-1 pb-[calc(4rem+env(safe-area-inset-bottom))] space-y-7">
                            {/* 1. 오늘의 할 일 */}
                            <div>
                                <p className="text-base md:text-lg leading-tight pb-2 font-bold">
                                    오늘의 할 일
                                </p>
                                <TodayTasksCard
                                    onClick={() => handleClick("todos")}
                                    userId={me?.id}
                                />
                            </div>

                            {/* 2. BMI 카드 */}
                            <div>
                                <BmiCard
                                    bmiData={bmiData}
                                    onProfileClick={() =>
                                        handleClick("profile")
                                    }
                                    onWeightClick={() =>
                                        navigate("/health/weight")
                                    }
                                />
                            </div>

                            {/* 3. 체중 카드 */}
                            <div
                                onClick={() => navigate("/health/body-fat")}
                            >
                                <p className="text-base md:text-lg leading-tight pb-2 font-bold">
                                    체지방량 기록
                                </p>
                                {bodyFatMonth.length > 0 ? (
                                    <WeightChartCard
                                        userId={me?.id}
                                        color="#B48CF2"
                                        series={bodyFatSeries}
                                        latest={latestBodyFat}
                                    />
                                ) : (
                                    <EmptyRecordCard
                                        message="체지방량 기록이 없습니다."
                                        onClick={() =>
                                            navigate("/health/body-fat")
                                        }
                                    />
                                )}
                            </div>

                            <div
                                onClick={() => navigate("/health/weight")}
                            >
                                <p className="text-base md:text-lg leading-tight pb-2 font-bold">
                                    체중 기록
                                </p>
                                {weightMonth.length > 0 ? (
                                    <WeightChartCard
                                        userId={me?.id}
                                        color="#7EDBB6"
                                        series={weightSeries}
                                        latest={latestWeight}
                                    />
                                ) : (
                                    <EmptyRecordCard
                                        message="체중 기록이 없습니다."
                                        onClick={() =>
                                            navigate("/health/weight")
                                        }
                                    />
                                )}
                            </div>

                            {/* 4. 최근 운동 */}
                            <div>
                                <p className="text-base md:text-lg leading-tight pb-2 font-bold">
                                    최근 운동
                                </p>
                                <RecentWorkoutsCard
                                    userId={me?.id}
                                    EmptyCard={EmptyRecordCard}
                                    onEmptyClick={() => navigate("/health/workout")}
                                />
                            </div>

                            {/* 🔹 추가된 건강 관리 (레이더 차트) 영역 */}
                            <div>
                                <p className="text-base md:text-lg leading-tight pb-2 font-bold text-center">
                                    건강 관리
                                </p>
                                <FitnessRadarCard
                                    userName={me?.name ?? "회원"}
                                    score={dashboardScore?.averageScore}
                                    data={dashboardScore?.radar}
                                />
                            </div>

                            <HealthScoreCard
                                strengthScore={dashboardScore?.strengthScore}
                                strengthDiff={dashboardScore?.strengthDiff}
                                cardioScore={dashboardScore?.cardioScore}
                                cardioStatus={dashboardScore?.cardioStatus}
                            />
                            {/* 5. 혈압 & 혈당 좌우 배치 (그래프 카드 사용) */}
                            <div>
                                <p className="text-base md:text-lg leading-tight pb-2 font-bold">
                                    주요 건강 지표
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    {/* 왼쪽: 혈압 (최근 1개 그래프) */}
                                    <div
                                        className="flex flex-col"
                                        onClick={() =>
                                            navigate("/health/bp")
                                        }
                                    >
                                        {bpMonth.length > 0 ? (
                                            <div className="h-48 overflow-hidden rounded-2xl border border-gray-200">
                                                <HealthRecordsCard
                                                    userId={me?.id}
                                                    color="#ED6363"
                                                    series={bpSeries}
                                                    latest={latestBp}
                                                />
                                            </div>
                                        ) : (
                                            <EmptyRecordCard
                                                message="혈압 기록 +"
                                                className="h-48"
                                            />
                                        )}
                                    </div>

                                    {/* 오른쪽: 혈당 (최근 1개 그래프) */}
                                    <div
                                        className="flex flex-col"
                                        onClick={() =>
                                            navigate("/health/glucose")
                                        }
                                    >
                                        {glucoseMonth.length > 0 ? (
                                            <div className="h-48 overflow-hidden rounded-2xl border border-gray-200">
                                                <GlucoseCard
                                                    userId={me?.id}
                                                    color="#E9D045"
                                                    series={glucoseSeries}
                                                    latest={latestGlucose}
                                                />
                                            </div>
                                        ) : (
                                            <EmptyRecordCard
                                                message="혈당 기록 +"
                                                className="h-48"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </AuthCard>
            )}

            <BottomNav className="h-16" />
        </main>
    );
}
